import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgMultiLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BranchInfo } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';

export interface GroupBranchOption {
  id: string;
  name: string;
  type: 'group' | 'option';
  items?: BranchInfo[];
  branch?: string;
}

@Component({
  selector: 'app-multi-branch-selector',
  imports: [
    NgIf,
    NgSelectComponent,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    NgMultiLabelTemplateDirective,
  ],
  templateUrl: './multi-branch-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiBranchSelectorComponent implements OnInit, OnDestroy {
  private readonly branchService = inject(BranchService);

  @Output() branchesSelected = new EventEmitter<BranchInfo[]>();

  getBranchesSubscription: Subscription = new Subscription();
  allSelected = false;
  branches: BranchInfo[] = [];
  formControl = new FormControl<string[]>([]);
  groupedBranches: GroupBranchOption[] = [];
  placeholder = 'Select Branches...';

  constructor() {
    this.formControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((selectedIds) => {
        if (!selectedIds || selectedIds.length === 0) {
          this.branchService.clearSelectedBranches();
          this.branchesSelected.emit([]);
          return;
        }

        const selectedBranches = this.branches.filter((branch) =>
          selectedIds.includes(branch.id!)
        );

        if (selectedBranches.length > 0) {
          this.branchService.setSelectedBranches(selectedBranches);
          this.branchesSelected.emit(selectedBranches);
        }
      });
  }

  ngOnInit(): void {
    this.getBranchesSubscription = this.branchService.getBranches().subscribe({
      next: (branches) => {
        this.branches = branches;
        this.buildGroupedBranches(branches);

        this.branchService
          .getSelectedBranches()
          .pipe(take(1))
          .subscribe((selectedBranches) => {
            if (selectedBranches.length > 0 && branches.length > 0) {
              const validIds = selectedBranches
                .filter((selected) => branches.some((b) => b.id === selected.id))
                .map((b) => b.id!);

              if (validIds.length > 0) {
                this.formControl.patchValue(validIds, { emitEvent: false });
                this.updateAllSelectedFlag();
              }
            }
          });
      },
      error: (error) => {
        console.error('Error fetching branches:', error);
      },
    });
  }

  ngOnDestroy(): void {
    this.getBranchesSubscription.unsubscribe();
  }

  onBlur(): void {
    this.formControl.markAsTouched();
  }

  onSelectionChange(_: unknown): void {
    this.updateAllSelectedFlag();
  }

  clearAll(): void {
    this.formControl.patchValue([]);
    this.branchService.clearSelectedBranches();
    this.branchesSelected.emit([]);
    this.allSelected = false;
  }

  private buildGroupedBranches(branches: BranchInfo[]): void {
    this.groupedBranches = branches.map((branch) => ({
      id: branch.id!,
      name: branch.fullBranchName || branch.name || '',
      type: 'option' as const,
      branch: branch.brandId,
    }));
  }

  private updateAllSelectedFlag(): void {
    const selectedCount = this.formControl.value?.length || 0;
    const totalOptions = this.groupedBranches.filter((g) => g.type === 'option').length;
    this.allSelected = selectedCount > 0 && selectedCount === totalOptions;
  }
}
